import * as R from 'remeda';
import * as Sentry from '@sentry/react';
import {
  currentCodeVersion,
  sentryDsn,
  sentryEnvironment,
  shouldSentry,
} from '../constants/config';
import { classifyEvent } from './classify';
import { withFingerprint } from './event-enhancers/withFingerprint';
import { withRequestId } from './event-enhancers/withRequestId';
import { withSmartlook } from './event-enhancers/withSmartlook';
import { withTransactionName } from './event-enhancers/withTransactionName';
import { POLICIES } from './policy';
import { shouldSendEvent } from './sampling';

/**
 * Endpoints whose breadcrumbs we don't want to attach payloads to
 * (sensitive data, large payloads).
 */
const ignoredBreadcrumbPatterns = [/\/auth\//, /\/oauth\//];

const isIgnoredBreadcrumbEndpoint = (url: string | null | undefined) =>
  typeof url === 'string' && ignoredBreadcrumbPatterns.some(p => p.test(url));

/**
 * Status codes whose request/response payloads we attach to the breadcrumb
 * trail. Matches develop's `loggedApiErrorCodes = [400]`: we only enrich the
 * breadcrumbs we'd actually report on, so we never ship more data to Sentry
 * than develop does (e.g. 5xx response bodies riding along on a kept event).
 */
const breadcrumbStatusCodesWithPayloads = [400];

export const initSentry = () => {
  Sentry.init({
    enabled: shouldSentry,
    normalizeDepth: 6,
    dsn: sentryDsn,
    environment: sentryEnvironment,
    release: currentCodeVersion,
    integrations: [Sentry.extraErrorDataIntegration()],
    beforeBreadcrumb: (breadcrumb, hint) => {
      // For logged API errors, try logging payloads and responses. There are
      // limits to how much Sentry can ingest, so very large payloads may still
      // be truncated.
      if (
        breadcrumb.category === 'xhr' &&
        breadcrumbStatusCodesWithPayloads.includes(
          breadcrumb.data?.['status_code']
        ) &&
        !isIgnoredBreadcrumbEndpoint(breadcrumb.data?.['url'])
      ) {
        const data = {
          // To make sure response is logged first in case payload is too large
          _response: {
            response: hint?.['xhr']?.response,
            responseText: hint?.['xhr']?.responseText,
          },
          payload: hint?.['input'],
        };

        return { ...breadcrumb, data };
      }

      return breadcrumb;
    },
    beforeSend: (event, hint) => {
      // 1. Classify cheaply based on the original exception / event message.
      const category = classifyEvent(event, hint);
      const policy = POLICIES[category];

      if (policy.type === 'drop') return null;

      // 2. Enhance (fingerprint, transaction, request id, smartlook).
      //    Sampling reads the fingerprint, so it must run after `withFingerprint`.
      const [enhancedEvent] = R.pipe(
        [event, hint],
        withSmartlook,
        withFingerprint,
        withRequestId,
        withTransactionName
      );

      // 3. Apply per-category sampling. The first event per fingerprint per
      //    session always passes; subsequent ones are sampled at `policy.rate`.
      if (!shouldSendEvent(enhancedEvent, policy)) return null;

      // 4. Tag with category so alert rules can filter on it.
      return {
        ...enhancedEvent,
        tags: { ...enhancedEvent.tags, category },
      };
    },
    // Regex-based message filters complement the structural classifier in
    // `beforeSend`. Use this list only for known noise that's easier to
    // match by message than by error type.
    ignoreErrors: [
      // https://trackjs.com/javascript-errors/object-not-found-matching-id-methodname-paramcount/
      /Object Not Found Matching Id/,
      // DataGrid row lookup errors after a stale render.
      /No row with id #(\d+) found/,
      // Request aborts (navigation away, manual cancel). Not a FE bug.
      /ECONNABORTED/,
    ],
  });

  return Sentry;
};
